import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { Promo, PromoAutoReward, PromoStats, PromoToken, PromoValidateResult } from '../_models/promo';

@Injectable({ providedIn: 'root' })
export class PromoService {

    constructor(private http: HttpClient) {}

    getAll() {
        return this.http.get<Promo[]>(`${environment.apiUrl}/promos/getAll`);
    }

    create(promo: Promo) {
        return this.http.post<Promo>(`${environment.apiUrl}/promos/create`, promo);
    }

    update(promo: Promo) {
        return this.http.put<Promo>(`${environment.apiUrl}/promos/update`, promo);
    }

    delete(id: string) {
        return this.http.delete(`${environment.apiUrl}/promos/${id}`);
    }

    launch(promoId: string, customers: any[], channels: 'sms' | 'email' | 'both' = 'both') {
        return this.http.post<{ tokensIssued: number; smsSent: number; emailSent: number; emailSkipped: number }>(
            `${environment.apiUrl}/promos/launch/${promoId}`,
            { customers, channels }
        );
    }

    validate(token: string) {
        return this.http.post<PromoValidateResult>(`${environment.apiUrl}/promos/validate`, { token });
    }

    redeem(tokenId: string | null, orderId: string, redeemedAmount: number, promoId?: string) {
        return this.http.post<{ success: boolean; error?: string }>(`${environment.apiUrl}/promos/redeem`, {
            tokenId, orderId, redeemedAmount, promoId
        });
    }

    getStats(promoId: string) {
        return this.http.get<PromoStats>(`${environment.apiUrl}/promos/stats/${promoId}`);
    }

    getTokens(promoId: string) {
        return this.http.get<PromoToken[]>(`${environment.apiUrl}/promos/tokens/${promoId}`);
    }

    /** Aggregate visit/redemption counts per customer for the current org */
    getCustomerStats() {
        return this.http.get<{ customerId: string; visits: number; redemptions: number }[]>(
            `${environment.apiUrl}/promos/customerStats`
        );
    }

    /** Increment visit count for customerId across all active visit-based promos */
    recordVisit(customerId: string) {
        return this.http.post<any[]>(`${environment.apiUrl}/promos/recordVisit`, { customerId });
    }

    /** Return any earned auto-rewards the customer qualifies for at checkout */
    checkAutoRewards(customerId: string) {
        return this.http.post<PromoAutoReward[]>(`${environment.apiUrl}/promos/checkAutoRewards`, { customerId });
    }

    /** Mark an auto-reward as redeemed and decrement the visit counter */
    redeemAutoReward(promoId: string, customerId: string, orderId: string) {
        return this.http.post<{ success: boolean; newVisitCount?: number }>(
            `${environment.apiUrl}/promos/redeemAutoReward`,
            { promoId, customerId, orderId }
        );
    }

    /** Get the configured intro promo for this org (if any) */
    getIntroPromo() {
        return this.http.get<{ promo: Promo | null; introConfig: any }>(`${environment.apiUrl}/promos/introPromo`);
    }
}
